/**
 * MiControlGestiones — página dedicada para que el perito gestione su
 * actividad diaria: control horario (fichajes), kilometraje, horario
 * laborable y vacaciones/ausencias.
 *
 * Cada sección permite exportar PDF y Excel desde
 * `GET /api/me/control-gestiones/export?section=<X>&format=<pdf|xlsx>`.
 *
 * Los tipos de fichaje y las fechas están en español:
 *   clock_in → "Entrada", clock_out → "Salida",
 *   break_start → "Inicio pausa", break_end → "Fin pausa".
 *   Fechas YYYY-MM-DD → DD/MM/AAAA.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Clock, MapPin, Briefcase, Calendar, SignIn, SignOut, Coffee,
  PlusCircle, PaperPlaneTilt, ListChecks, Trash, FilePdf, FileXls,
  Receipt, Camera, Spinner, Check, X as XIcon, Users, PencilSimple, Airplane,
} from "@phosphor-icons/react";
import WeekScheduleOverridesCard from "@/components/calendar/WeekScheduleOverridesCard";

// ============================================================
// HELPERS
// ============================================================
const TYPE_LABELS = {
  clock_in: "Entrada",
  clock_out: "Salida",
  break_start: "Inicio pausa",
  break_end: "Fin pausa",
};

const DAY_LABELS_ES = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const MONTH_LABELS_ES = ["enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

function MONTH_NOW() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function TODAY() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
const RATE_DEFAULT = 0.35;

function fmtHours(min) {
  const h = Math.floor((min || 0) / 60);
  const m = (min || 0) % 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
}
function fmtTime(iso) {
  try { return new Date(iso).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
}
/** YYYY-MM-DD → DD/MM/AAAA */
function fmtDateES(iso) {
  if (!iso || iso.length < 10) return "—";
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
}
function fmtMonthES(yyyymm) {
  if (!yyyymm || yyyymm.length !== 7) return yyyymm;
  return `${MONTH_LABELS_ES[parseInt(yyyymm.slice(5, 7), 10) - 1]} de ${yyyymm.slice(0, 4)}`;
}
function diaSemanaES(isoDate) {
  try { return DAY_LABELS_ES[new Date(isoDate).getDay()]; } catch { return ""; }
}

/** Descarga el blob del endpoint export con el filename del header. */
async function downloadExport({ section, format, params = {} }) {
  try {
    const qs = new URLSearchParams({ section, format, ...params }).toString();
    const r = await api.get(`/me/control-gestiones/export?${qs}`, { responseType: "blob" });
    const cd = r.headers["content-disposition"] || "";
    const m = cd.match(/filename="?([^"]+)"?/);
    const fname = m ? m[1] : `${section}.${format}`;
    const url = URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement("a");
    a.href = url; a.download = fname;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Error generando el archivo");
  }
}

// ============================================================
// PÁGINA
// ============================================================
export default function MiControlGestiones() {
  const { user } = useAuth();
  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-5 md:py-6 space-y-5" data-testid="control-gestiones-page">
      <header className="flex items-center gap-3">
        <span className="icon-chip icon-chip-brand"><Clock className="w-4 h-4" weight="regular" /></span>
        <div className="flex-1">
          <h1 className="page-title">Mi control y gestiones</h1>
          <div className="help-text">Tu jornada, kilometraje, horario laborable y vacaciones.</div>
        </div>
      </header>

      <JornadaSection />
      <KmExpensesSection />
      <OtrosGastosSection />
      <ScheduleSection user={user} />
      <WeekOverridesSection user={user} />
      <VacationsSection user={user} />
      <TeamVacationsSection user={user} />
    </div>
  );
}

// ============================================================
// SUB-COMPONENTES VISUALES
// ============================================================
function Card({ title, icon, tone = "brand", children, actions }) {
  return (
    <section className="bg-white rounded-[10px] border border-border">
      <header className="px-4 md:px-5 py-3 flex items-center gap-2.5 border-b border-border">
        <span className={`icon-chip icon-chip-${tone}`}>{icon}</span>
        <h2 className="section-title flex-1">{title}</h2>
        {actions ? <div className="flex items-center gap-1.5">{actions}</div> : null}
      </header>
      <div className="p-4 md:p-5">{children}</div>
    </section>
  );
}

function ExportButtons({ section, params = {} }) {
  return (
    <>
      <button
        onClick={() => downloadExport({ section, format: "pdf", params })}
        data-testid={`export-${section}-pdf`}
        className="text-[11.5px] inline-flex items-center gap-1 px-2.5 h-7 rounded-md border border-border bg-white text-foreground hover:bg-background font-medium"
        title="Exportar a PDF"
      >
        <FilePdf className="w-3.5 h-3.5" weight="regular" /> PDF
      </button>
      <button
        onClick={() => downloadExport({ section, format: "xlsx", params })}
        data-testid={`export-${section}-xlsx`}
        className="text-[11.5px] inline-flex items-center gap-1 px-2.5 h-7 rounded-md border border-border bg-white text-foreground hover:bg-background font-medium"
        title="Exportar a Excel"
      >
        <FileXls className="w-3.5 h-3.5" weight="regular" /> Excel
      </button>
    </>
  );
}

function Field({ label, className = "", children }) {
  return (
    <div className={className}>
      <label className="field-label block mb-1">{label}</label>
      {children}
    </div>
  );
}

// ============================================================
// 1) CONTROL HORARIO
// ============================================================
function JornadaSection() {
  const [today, setToday] = useState(null);
  const [monthData, setMonthData] = useState(null);
  const [month, setMonth] = useState(MONTH_NOW());

  const refresh = useCallback(async () => {
    const r = await api.get("/me/jornada/today");
    setToday(r.data);
    const m = await api.get(`/me/jornada/month?month=${month}`);
    setMonthData(m.data);
  }, [month]);

  useEffect(() => { refresh(); }, [refresh]);

  const clock = async (type) => {
    try {
      await api.post("/me/jornada/clock", { type });
      const labels = {
        clock_in: "Fichaje de entrada registrado",
        clock_out: "Fichaje de salida registrado",
        break_start: "Inicio de pausa registrado",
        break_end: "Fin de pausa registrado",
      };
      toast.success(labels[type] || "Fichaje registrado");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error registrando fichaje");
    }
  };

  const state = today?.state || "off";
  const stateInfo = {
    working: { label: "Trabajando", cls: "status-pill-success" },
    on_break: { label: "En pausa", cls: "status-pill-amber" },
    off: { label: "Fuera de jornada", cls: "status-pill-gray" },
  }[state] || { label: "Fuera de jornada", cls: "status-pill-gray" };

  return (
    <Card
      title="Control horario"
      tone="brand"
      icon={<Clock className="w-4 h-4" weight="regular" />}
      actions={<ExportButtons section="jornada" params={{ month }} />}
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <span className={`status-pill ${stateInfo.cls} mb-1.5`}>{stateInfo.label}</span>
          <div className="text-[13px] text-foreground mt-1">
            Hoy llevas <b>{today ? fmtHours(today.worked_minutes) : "—"}</b> trabajados.
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {state === "off" && (
            <button onClick={() => clock("clock_in")} data-testid="clock-in"
              className="bg-status-success-fg hover:opacity-90 text-white text-[13px] px-3 h-9 rounded-md font-semibold inline-flex items-center gap-1.5">
              <SignIn className="w-4 h-4" weight="bold" /> Entrada
            </button>
          )}
          {state === "working" && (
            <>
              <button onClick={() => clock("break_start")} data-testid="break-start"
                className="bg-accent-amber-fg hover:opacity-90 text-white text-[13px] px-3 h-9 rounded-md font-semibold inline-flex items-center gap-1.5">
                <Coffee className="w-4 h-4" weight="bold" /> Pausa
              </button>
              <button onClick={() => clock("clock_out")} data-testid="clock-out"
                className="bg-status-error-fg hover:opacity-90 text-white text-[13px] px-3 h-9 rounded-md font-semibold inline-flex items-center gap-1.5">
                <SignOut className="w-4 h-4" weight="bold" /> Salida
              </button>
            </>
          )}
          {state === "on_break" && (
            <button onClick={() => clock("break_end")} data-testid="break-end"
              className="bg-accent-blue-fg hover:opacity-90 text-white text-[13px] px-3 h-9 rounded-md font-semibold inline-flex items-center gap-1.5">
              <SignIn className="w-4 h-4" weight="bold" /> Reanudar
            </button>
          )}
        </div>
      </div>

      {today?.events?.length > 0 && (
        <div className="mb-4 bg-background rounded-md p-3 border border-border text-[12px]">
          <div className="field-label mb-2">Fichajes de hoy</div>
          <div className="flex flex-wrap gap-2">
            {today.events.map((ev) => (
              <span key={ev.id} className="bg-white border border-border px-2 py-1 rounded text-[12px]">
                {fmtTime(ev.timestamp)} · {TYPE_LABELS[ev.type] || ev.type}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <label className="field-label mb-0">Resumen mensual · {fmtMonthES(month)}</label>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="jornada-month" />
      </div>
      {monthData && (
        <div className="text-[13px]">
          <div className="mb-2">
            Total: <b>{fmtHours(monthData.total_minutes)}</b>
            {" "}({monthData.total_hours.toFixed(1)} h en {monthData.days?.length || 0} días)
          </div>
          {monthData.days?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead className="bg-background text-[11.5px]">
                  <tr>
                    <th className="text-left py-1.5 px-2 font-semibold text-muted">Día</th>
                    <th className="text-left py-1.5 px-2 font-semibold text-muted">Fecha</th>
                    <th className="text-right py-1.5 px-2 font-semibold text-muted">Horas</th>
                    <th className="text-left py-1.5 px-2 font-semibold text-muted">Fichajes</th>
                  </tr>
                </thead>
                <tbody>
                  {monthData.days.map((d) => (
                    <tr key={d.date} className="border-t border-border">
                      <td className="py-1.5 px-2">{diaSemanaES(d.date)}</td>
                      <td className="py-1.5 px-2">{fmtDateES(d.date)}</td>
                      <td className="py-1.5 px-2 text-right font-semibold">{fmtHours(d.worked_minutes)}</td>
                      <td className="py-1.5 px-2 text-muted">
                        {d.events.map((e) => `${fmtTime(e.timestamp)} ${TYPE_LABELS[e.type] || e.type}`).join(" · ")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="help-text">Sin fichajes este mes.</p>
          )}
        </div>
      )}
      <p className="help-text italic mt-3">
        Los registros se conservan durante <b>4 años</b> conforme al RD-Ley 8/2019.
      </p>
    </Card>
  );
}

// ============================================================
// 2) KILOMETRAJE
// ============================================================
function KmExpensesSection() {
  const [items, setItems] = useState([]);
  const [totalKms, setTotalKms] = useState(0);
  const [totalEur, setTotalEur] = useState(0);
  const [month, setMonth] = useState(MONTH_NOW());
  const [draft, setDraft] = useState({ fecha: TODAY(), ruta: "", kms: "", tarifa: RATE_DEFAULT });

  const load = useCallback(async () => {
    const r = await api.get(`/me/km-expenses?month=${month}`);
    setItems(r.data.items || []);
    setTotalKms(r.data.total_kms || 0);
    setTotalEur(r.data.total_eur || 0);
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!draft.ruta || !draft.kms) { toast.error("Ruta y kilómetros son obligatorios"); return; }
    try {
      await api.post("/me/km-expenses", {
        fecha: draft.fecha, ruta: draft.ruta.trim(),
        kms: parseFloat(draft.kms), tarifa_eur_km: parseFloat(draft.tarifa) || RATE_DEFAULT,
      });
      toast.success("Trayecto añadido");
      setDraft({ fecha: TODAY(), ruta: "", kms: "", tarifa: parseFloat(draft.tarifa) || RATE_DEFAULT });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error guardando"); }
  };

  const del = async (id) => {
    if (!confirm("¿Borrar este trayecto?")) return;
    try { await api.delete(`/me/km-expenses/${id}`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const submit = async () => {
    const pending = items.filter((i) => i.status === "borrador");
    if (!pending.length) { toast.error("No hay trayectos en borrador para enviar"); return; }
    if (!confirm(`Enviar ${pending.length} trayecto(s) de ${fmtMonthES(month)} al Gestor Senior para minutar (${pending.reduce((s, i) => s + i.importe, 0).toFixed(2)} €)?`)) return;
    try {
      const r = await api.post("/me/km-expenses/submit", { month });
      toast.success(`Enviado a ${r.data.notified_gestores} gestor(es) · ${r.data.total_eur} €`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error enviando"); }
  };

  const importePreview = (parseFloat(draft.kms) || 0) * (parseFloat(draft.tarifa) || RATE_DEFAULT);

  return (
    <Card
      title="Kilometraje del mes"
      tone="teal"
      icon={<MapPin className="w-4 h-4" weight="regular" />}
      actions={<ExportButtons section="km" params={{ month }} />}
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="km-month" />
        <div className="text-[13px]">
          Total: <b>{totalKms.toFixed(0)} km</b> · <b className="text-brand">{totalEur.toFixed(2)} €</b>
        </div>
      </div>

      <div className="bg-background rounded-md p-3 mb-4 border border-border">
        <div className="grid grid-cols-12 gap-2 items-end">
          <Field label="Fecha" className="col-span-12 sm:col-span-3"><input type="date" value={draft.fecha} onChange={(e) => setDraft({ ...draft, fecha: e.target.value })} data-testid="km-fecha" /></Field>
          <Field label="Ruta" className="col-span-12 sm:col-span-5"><input type="text" placeholder="Salamanca - Benavente - Salamanca" value={draft.ruta} onChange={(e) => setDraft({ ...draft, ruta: e.target.value })} data-testid="km-ruta" /></Field>
          <Field label="Km" className="col-span-6 sm:col-span-2"><input type="number" placeholder="148" value={draft.kms} onChange={(e) => setDraft({ ...draft, kms: e.target.value })} className="text-right" data-testid="km-kms" /></Field>
          <Field label="€/km" className="col-span-6 sm:col-span-2"><input type="number" step="0.01" value={draft.tarifa} onChange={(e) => setDraft({ ...draft, tarifa: e.target.value })} className="text-right" data-testid="km-tarifa" /></Field>
        </div>
        <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
          <div className="text-[12px] text-muted">Importe del trayecto: <b className="text-brand">{importePreview.toFixed(2)} €</b></div>
          <button onClick={add} data-testid="km-add" className="bg-brand text-white text-[13px] px-3 h-9 rounded-md font-semibold inline-flex items-center gap-1.5">
            <PlusCircle className="w-4 h-4" weight="bold" /> Añadir trayecto
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <p className="help-text text-center py-4">Sin trayectos en {fmtMonthES(month)}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-background text-[11.5px]">
              <tr>
                <th className="text-left py-2 px-2 font-semibold text-muted">Fecha</th>
                <th className="text-left py-2 px-2 font-semibold text-muted">Ruta</th>
                <th className="text-right py-2 px-2 font-semibold text-muted">Km</th>
                <th className="text-right py-2 px-2 font-semibold text-muted">€</th>
                <th className="text-center py-2 px-2 font-semibold text-muted">Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className="border-t border-border">
                  <td className="py-1.5 px-2">{fmtDateES(i.fecha)}</td>
                  <td className="py-1.5 px-2">{i.ruta}</td>
                  <td className="py-1.5 px-2 text-right">{i.kms.toFixed(0)}</td>
                  <td className="py-1.5 px-2 text-right font-semibold">{i.importe.toFixed(2)} €</td>
                  <td className="py-1.5 px-2 text-center"><KmStatusBadge status={i.status} /></td>
                  <td className="py-1.5 px-2 text-right">
                    {i.status === "borrador" && (
                      <button onClick={() => del(i.id)} className="text-status-error-fg hover:opacity-80" title="Borrar" data-testid={`km-del-${i.id}`}>
                        <Trash className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 flex justify-end">
        <button onClick={submit} disabled={items.filter((i) => i.status === "borrador").length === 0}
          data-testid="km-submit" className="bg-foreground text-white text-[13px] px-4 h-9 rounded-md font-semibold inline-flex items-center gap-2 disabled:opacity-40">
          <PaperPlaneTilt className="w-4 h-4" weight="bold" /> Enviar a Gestor Senior para minutar
        </button>
      </div>
    </Card>
  );
}

function KmStatusBadge({ status }) {
  const map = {
    borrador: { cls: "status-pill-gray", label: "Borrador" },
    enviado: { cls: "status-pill-blue", label: "Enviado" },
    aprobado: { cls: "status-pill-success", label: "Aprobado" },
    liquidado: { cls: "status-pill-teal", label: "Liquidado" },
    rechazado: { cls: "status-pill-red", label: "Rechazado" },
  };
  const s = map[status] || map.borrador;
  return <span className={`status-pill ${s.cls}`}>{s.label}</span>;
}

// ============================================================
// 2.b) OTROS GASTOS — parking, ORA, comida, peaje, otros
// ============================================================
const GASTO_CATS = [
  { k: "parking", label: "Parking" },
  { k: "ora", label: "ORA / Zona azul" },
  { k: "comida", label: "Comida" },
  { k: "peaje", label: "Peaje" },
  { k: "otros", label: "Otros" },
];

function OtrosGastosSection() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({
    fecha: new Date().toISOString().slice(0, 10),
    categoria: "parking",
    importe: "",
    descripcion: "",
  });
  const [ocrModal, setOcrModal] = useState(null);   // { file, preview, busy, result }

  const total = useMemo(() => items.reduce((s, i) => s + (i.importe || 0), 0), [items]);

  const load = useCallback(async () => {
    const r = await api.get(`/me/gastos?month=${month}`);
    setItems(r.data.items || []);
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!draft.fecha || !draft.importe || parseFloat(draft.importe) <= 0) {
      toast.error("Indica fecha y un importe válido");
      return;
    }
    try {
      await api.post("/me/gastos", {
        fecha: draft.fecha,
        categoria: draft.categoria,
        importe: parseFloat(draft.importe),
        descripcion: draft.descripcion || null,
      });
      setDraft({ ...draft, importe: "", descripcion: "" });
      load();
      toast.success("Gasto añadido");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const del = async (id) => {
    try { await api.delete(`/me/gastos/${id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const submit = async () => {
    try {
      const r = await api.post("/me/gastos/submit", { month });
      toast.success(`Enviados ${r.data.submitted} gastos (${r.data.total_eur.toFixed(2)} €)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  // OCR del ticket
  const onPickTicket = (file) => {
    if (!file) return;
    const preview = URL.createObjectURL(file);
    setOcrModal({ file, preview, busy: true, result: null });
    const fd = new FormData();
    fd.append("file", file);
    api.post("/me/gastos/ocr-ticket", fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => {
        setOcrModal((m) => m ? { ...m, busy: false, result: r.data } : null);
      })
      .catch((e) => {
        setOcrModal((m) => m ? { ...m, busy: false, result: { error: e?.response?.data?.detail || "Error OCR" } } : null);
      });
  };

  const confirmOcr = (importe, descripcion, categoria) => {
    if (!importe || importe <= 0) {
      toast.error("Indica un importe");
      return;
    }
    setDraft((d) => ({
      ...d,
      importe: String(importe),
      descripcion: descripcion || d.descripcion,
      categoria: categoria || d.categoria,
    }));
    if (ocrModal?.preview) URL.revokeObjectURL(ocrModal.preview);
    setOcrModal(null);
    toast.info("Datos cargados. Revisa y pulsa 'Añadir gasto'.");
  };

  return (
    <Card title="Otros gastos del mes"
          tone="amber"
          icon={<Receipt className="w-4 h-4" weight="regular" />}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                data-testid="gasto-month" />
        <div className="text-[13px]">
          Total mes: <b className="text-brand">{total.toFixed(2)} €</b> · {items.length} ticket{items.length === 1 ? "" : "s"}
        </div>
      </div>

      <div className="bg-background rounded-md p-3 mb-4 border border-border">
        <div className="grid grid-cols-12 gap-2 items-end">
          <Field label="Fecha" className="col-span-6 sm:col-span-3">
            <input type="date" value={draft.fecha}
                   onChange={(e) => setDraft({ ...draft, fecha: e.target.value })}
                   data-testid="gasto-fecha" />
          </Field>
          <Field label="Categoría" className="col-span-6 sm:col-span-3">
            <select value={draft.categoria}
                    onChange={(e) => setDraft({ ...draft, categoria: e.target.value })}
                    data-testid="gasto-categoria">
              {GASTO_CATS.map((c) => <option key={c.k} value={c.k}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="Importe (€)" className="col-span-6 sm:col-span-2">
            <input type="number" step="0.01" placeholder="4,50"
                   value={draft.importe}
                   onChange={(e) => setDraft({ ...draft, importe: e.target.value })}
                   className="text-right" data-testid="gasto-importe" />
          </Field>
          <Field label="Descripción" className="col-span-12 sm:col-span-4">
            <input type="text" placeholder="Parking centro Salamanca"
                   value={draft.descripcion}
                   onChange={(e) => setDraft({ ...draft, descripcion: e.target.value })}
                   data-testid="gasto-descripcion" />
          </Field>
        </div>
        <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
          <label className="text-[12px] inline-flex items-center gap-1.5 cursor-pointer bg-amber-50 border border-amber-200 px-3 h-9 rounded-md font-semibold text-amber-900 hover:bg-amber-100"
                 data-testid="gasto-ticket-trigger">
            <Camera className="w-4 h-4" weight="bold" /> Subir foto del ticket
            <input type="file" accept="image/*" capture="environment" className="hidden"
                   onChange={(e) => onPickTicket(e.target.files?.[0])}
                   data-testid="gasto-ticket-input" />
          </label>
          <button onClick={add} data-testid="gasto-add"
                  className="bg-brand text-white text-[13px] px-3 h-9 rounded-md font-semibold inline-flex items-center gap-1.5">
            <PlusCircle className="w-4 h-4" weight="bold" /> Añadir gasto
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <p className="help-text text-center py-4">Sin gastos en {fmtMonthES(month)}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-background text-[11.5px]">
              <tr>
                <th className="text-left py-2 px-2 font-semibold text-muted">Fecha</th>
                <th className="text-left py-2 px-2 font-semibold text-muted">Categoría</th>
                <th className="text-left py-2 px-2 font-semibold text-muted">Descripción</th>
                <th className="text-right py-2 px-2 font-semibold text-muted">€</th>
                <th className="text-center py-2 px-2 font-semibold text-muted">Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className="border-t border-border" data-testid={`gasto-row-${i.id}`}>
                  <td className="py-1.5 px-2">{fmtDateES(i.fecha)}</td>
                  <td className="py-1.5 px-2">{GASTO_CATS.find((c) => c.k === i.categoria)?.label || i.categoria}</td>
                  <td className="py-1.5 px-2 text-muted">{i.descripcion || "—"}</td>
                  <td className="py-1.5 px-2 text-right font-semibold">{i.importe.toFixed(2)} €</td>
                  <td className="py-1.5 px-2 text-center"><KmStatusBadge status={i.status} /></td>
                  <td className="py-1.5 px-2 text-right">
                    {i.status === "borrador" && (
                      <button onClick={() => del(i.id)} className="text-status-error-fg hover:opacity-80"
                              title="Borrar" data-testid={`gasto-del-${i.id}`}>
                        <Trash className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 flex justify-end">
        <button onClick={submit}
                disabled={items.filter((i) => i.status === "borrador").length === 0}
                data-testid="gasto-submit"
                className="bg-foreground text-white text-[13px] px-4 h-9 rounded-md font-semibold inline-flex items-center gap-2 disabled:opacity-40">
          <PaperPlaneTilt className="w-4 h-4" weight="bold" /> Enviar a Gestor Senior para minutar
        </button>
      </div>

      {ocrModal && <OcrTicketModal modal={ocrModal} onClose={() => {
        if (ocrModal.preview) URL.revokeObjectURL(ocrModal.preview);
        setOcrModal(null);
      }} onConfirm={confirmOcr} />}
    </Card>
  );
}

function OcrTicketModal({ modal, onClose, onConfirm }) {
  const { preview, busy, result } = modal;
  const [importe, setImporte] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [categoria, setCategoria] = useState("parking");

  useEffect(() => {
    if (result && !result.error) {
      if (result.importe != null) setImporte(String(result.importe));
      if (result.comercio) setDescripcion(result.comercio);
    }
  }, [result]);

  const confidence = result?.confidence ?? 0;
  const confLabel = confidence >= 0.8 ? "Alta" : confidence >= 0.5 ? "Media" : "Baja";
  const confColor = confidence >= 0.8 ? "text-emerald-700" : confidence >= 0.5 ? "text-amber-700" : "text-rose-700";

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
         onClick={onClose} data-testid="ocr-ticket-modal">
      <div className="bg-white rounded-lg max-w-md w-full p-4 shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-[15px]">Importe detectado en el ticket</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-zinc-100"><XIcon className="w-4 h-4" /></button>
        </div>
        <img src={preview} alt="ticket"
             className="w-full max-h-48 object-contain rounded border border-border bg-zinc-50 mb-3" />
        {busy && (
          <div className="flex items-center gap-2 text-[13px] text-amber-700 justify-center py-4">
            <Spinner className="w-5 h-5 animate-spin" /> Leyendo el ticket con IA…
          </div>
        )}
        {!busy && result?.error && (
          <div className="text-rose-700 text-[12px] bg-rose-50 border border-rose-200 rounded p-2 mb-2"
               data-testid="ocr-error">
            ❌ {result.error}. Introduce el importe manualmente.
          </div>
        )}
        {!busy && result && !result.error && (
          <div className="text-[12px] text-muted mb-2"
               data-testid="ocr-confidence">
            Confianza IA: <b className={confColor}>{confLabel}</b> ({Math.round(confidence * 100)}%)
          </div>
        )}
        <div className="space-y-2">
          <Field label="Importe detectado (€) — revisa siempre">
            <input type="number" step="0.01" autoFocus
                   value={importe} onChange={(e) => setImporte(e.target.value)}
                   className="text-right text-[18px] font-bold"
                   data-testid="ocr-importe-input" />
          </Field>
          <Field label="Categoría">
            <select value={categoria} onChange={(e) => setCategoria(e.target.value)}
                    data-testid="ocr-categoria-select">
              {GASTO_CATS.map((c) => <option key={c.k} value={c.k}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="Descripción (opcional)">
            <input type="text" value={descripcion}
                   onChange={(e) => setDescripcion(e.target.value)}
                   placeholder="Parking de Salamanca centro"
                   data-testid="ocr-descripcion-input" />
          </Field>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <button onClick={onClose} className="text-[13px] px-3 py-2 text-muted hover:text-foreground">Cancelar</button>
          <button onClick={() => onConfirm(parseFloat(importe), descripcion, categoria)}
                  disabled={busy || !importe || parseFloat(importe) <= 0}
                  data-testid="ocr-confirm-btn"
                  className="bg-foreground text-white text-[13px] font-semibold px-4 py-2 rounded-md inline-flex items-center gap-1.5 disabled:opacity-40">
            <Check className="w-4 h-4" weight="bold" /> Confirmar y precargar
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// 3) HORARIO LABORABLE
// ============================================================
function ScheduleSection({ user }) {
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    api.get(`/calendar/schedule/${user.id}`).then((r) => setSchedule(r.data));
  }, [user?.id]);

  const save = async () => {
    setLoading(true);
    try { await api.put(`/calendar/schedule/${user.id}`, schedule); toast.success("Horario guardado"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };

  if (!schedule) {
    return (
      <Card title="Mis horarios laborables" tone="purple" icon={<Briefcase className="w-4 h-4" weight="regular" />}>
        <p className="help-text">Cargando…</p>
      </Card>
    );
  }

  const days = [
    { key: "lunes", label: "Lunes" }, { key: "martes", label: "Martes" },
    { key: "miercoles", label: "Miércoles" }, { key: "jueves", label: "Jueves" },
    { key: "viernes", label: "Viernes" }, { key: "sabado", label: "Sábado" },
    { key: "domingo", label: "Domingo" },
  ];

  return (
    <Card
      title="Mis horarios laborables"
      tone="purple"
      icon={<Briefcase className="w-4 h-4" weight="regular" />}
      actions={<ExportButtons section="schedule" />}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-background text-[11.5px]">
            <tr>
              <th className="text-left py-2 px-2 font-semibold text-muted">Día</th>
              <th className="text-center py-2 px-2 font-semibold text-muted">Trabajo</th>
              <th className="text-center py-2 px-2 font-semibold text-muted">Inicio</th>
              <th className="text-center py-2 px-2 font-semibold text-muted">Fin</th>
              <th className="text-center py-2 px-2 font-semibold text-muted">Pausa inicio</th>
              <th className="text-center py-2 px-2 font-semibold text-muted">Pausa fin</th>
            </tr>
          </thead>
          <tbody>
            {days.map(({ key, label }) => {
              const d = schedule[key] || {};
              const upd = (patch) => setSchedule({ ...schedule, [key]: { ...d, ...patch } });
              return (
                <tr key={key} className="border-t border-border">
                  <td className="py-1.5 px-2 font-medium">{label}</td>
                  <td className="py-1.5 px-2 text-center">
                    <input type="checkbox" checked={d.enabled || false} onChange={(e) => upd({ enabled: e.target.checked })} data-testid={`sched-${key}-enabled`} />
                  </td>
                  <td className="py-1.5 px-2 text-center"><input type="time" value={d.morning_start || ""} onChange={(e) => upd({ morning_start: e.target.value })} disabled={!d.enabled} className="!h-8 !text-[12px] !px-1.5 disabled:opacity-30" /></td>
                  <td className="py-1.5 px-2 text-center"><input type="time" value={d.afternoon_end || ""} onChange={(e) => upd({ afternoon_end: e.target.value })} disabled={!d.enabled} className="!h-8 !text-[12px] !px-1.5 disabled:opacity-30" /></td>
                  <td className="py-1.5 px-2 text-center"><input type="time" value={d.morning_end || ""} onChange={(e) => upd({ morning_end: e.target.value })} disabled={!d.enabled} className="!h-8 !text-[12px] !px-1.5 disabled:opacity-30" /></td>
                  <td className="py-1.5 px-2 text-center"><input type="time" value={d.afternoon_start || ""} onChange={(e) => upd({ afternoon_start: e.target.value })} disabled={!d.enabled} className="!h-8 !text-[12px] !px-1.5 disabled:opacity-30" /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex justify-end">
        <button onClick={save} disabled={loading} data-testid="sched-save" className="bg-foreground text-white text-[13px] px-4 h-9 rounded-md font-semibold disabled:opacity-50">
          {loading ? "Guardando…" : "Guardar horario"}
        </button>
      </div>
    </Card>
  );
}

// ============================================================
// 3b) EXCEPCIONES POR SEMANA ISO
// ============================================================
function WeekOverridesSection({ user }) {
  const [schedule, setSchedule] = useState(null);

  const load = useCallback(async () => {
    if (!user?.id) return;
    try {
      const r = await api.get(`/calendar/schedule/${user.id}`);
      setSchedule(r.data);
    } catch (err) {
      console.warn("No se pudo cargar el horario", err);
    }
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  if (!user?.id) return null;

  return (
    <WeekScheduleOverridesCard
      peritoId={user.id}
      schedule={schedule}
      onChanged={load}
    />
  );
}

// ============================================================
// 4) VACACIONES Y AUSENCIAS
// ============================================================
function VacationsSection({ user }) {
  const [list, setList] = useState([]);
  const [draft, setDraft] = useState({ start_date: "", end_date: "", concept: "Vacaciones" });

  const load = useCallback(async () => {
    if (!user?.id) return;
    const r = await api.get(`/calendar/schedule/${user.id}`);
    setList(r.data?.vacations || []);
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  const request = async () => {
    if (!draft.start_date || !draft.end_date) { toast.error("Fechas obligatorias"); return; }
    try {
      await api.post(`/calendar/schedule/${user.id}/vacations`, draft);
      toast.success("Solicitud enviada");
      setDraft({ start_date: "", end_date: "", concept: "Vacaciones" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  return (
    <Card
      title="Vacaciones y ausencias"
      tone="amber"
      icon={<Calendar className="w-4 h-4" weight="regular" />}
      actions={<ExportButtons section="vacations" />}
    >
      <div className="bg-background rounded-md p-3 mb-4 border border-border">
        <div className="grid grid-cols-12 gap-2 items-end">
          <Field label="Desde" className="col-span-12 sm:col-span-3"><input type="date" value={draft.start_date} onChange={(e) => setDraft({ ...draft, start_date: e.target.value })} data-testid="vac-start" /></Field>
          <Field label="Hasta" className="col-span-12 sm:col-span-3"><input type="date" value={draft.end_date} onChange={(e) => setDraft({ ...draft, end_date: e.target.value })} data-testid="vac-end" /></Field>
          <Field label="Concepto" className="col-span-12 sm:col-span-4"><input type="text" value={draft.concept} onChange={(e) => setDraft({ ...draft, concept: e.target.value })} data-testid="vac-concept" /></Field>
          <div className="col-span-12 sm:col-span-2">
            <button onClick={request} data-testid="vac-request" className="w-full bg-brand text-white text-[13px] px-3 h-9 rounded-md font-semibold inline-flex items-center justify-center gap-1.5">
              <ListChecks className="w-4 h-4" weight="bold" /> Solicitar
            </button>
          </div>
        </div>
      </div>
      {list.length === 0 ? (
        <p className="help-text text-center py-4">No tienes solicitudes registradas</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-background text-[11.5px]">
              <tr>
                <th className="text-left py-2 px-2 font-semibold text-muted">Desde</th>
                <th className="text-left py-2 px-2 font-semibold text-muted">Hasta</th>
                <th className="text-left py-2 px-2 font-semibold text-muted">Concepto</th>
                <th className="text-center py-2 px-2 font-semibold text-muted">Estado</th>
              </tr>
            </thead>
            <tbody>
              {list.map((v) => (
                <tr key={v.id} className="border-t border-border">
                  <td className="py-1.5 px-2">{fmtDateES(v.start_date)}</td>
                  <td className="py-1.5 px-2">{fmtDateES(v.end_date)}</td>
                  <td className="py-1.5 px-2">{v.concept}</td>
                  <td className="py-1.5 px-2 text-center"><VacStatusBadge status={v.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function VacStatusBadge({ status }) {
  const map = {
    requested: { cls: "status-pill-blue", label: "Solicitada" },
    pending: { cls: "status-pill-amber", label: "Pendiente" },
    approved: { cls: "status-pill-success", label: "Aprobada" },
    rejected: { cls: "status-pill-red", label: "Rechazada" },
  };
  const s = map[status] || map.pending;
  return <span className={`status-pill ${s.cls}`}>{s.label}</span>;
}


// ============================================================
// TEAM VACATIONS — Vista de equipo: agenda + saldos
// ============================================================
// Visible para CUALQUIER usuario autenticado. Pestañas:
//   • "Calendario" → vista mensual con avatars de quién está fuera
//   • "Saldos" → tabla con asignados/usados/pendientes/restantes
// Admin / gestor_senior pueden editar el total anual asignado.

function TeamVacationsSection({ user }) {
  const [tab, setTab] = useState("calendar");
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const canEdit = user && (user.role === "admin" || user.role === "gestor_senior");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/calendar/vacations/overview`, { params: { year } });
      setOverview(r.data);
    } catch {
      toast.error("No se pudieron cargar las vacaciones del equipo");
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card
      title="Vacaciones del equipo"
      icon={<Users className="w-4 h-4" weight="regular" />}
      actions={
        <div className="flex items-center gap-2">
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="text-[12px] border border-border rounded-md px-2 py-1"
            data-testid="team-vac-year"
          >
            {[year - 1, year, year + 1].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      }
    >
      <div className="flex gap-1 mb-4 border-b border-border">
        <TabBtn active={tab === "calendar"} onClick={() => setTab("calendar")} testid="team-vac-tab-calendar">
          <Calendar className="w-3.5 h-3.5" weight="regular" /> Calendario
        </TabBtn>
        <TabBtn active={tab === "balances"} onClick={() => setTab("balances")} testid="team-vac-tab-balances">
          <Airplane className="w-3.5 h-3.5" weight="regular" /> Saldos
        </TabBtn>
      </div>

      {loading ? (
        <div className="text-[13px] text-muted">Cargando…</div>
      ) : tab === "calendar" ? (
        <TeamVacationsCalendar overview={overview} year={year} />
      ) : (
        <TeamVacationsBalances overview={overview} canEdit={canEdit} onChanged={load} />
      )}
    </Card>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={
        "inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold transition border-b-2 -mb-px " +
        (active ? "text-orange-700 border-orange-500" : "text-muted border-transparent hover:text-foreground")
      }
    >{children}</button>
  );
}

// Helper: lista de fechas YYYY-MM-DD entre [s, e] inclusive
function* iterDates(s, e) {
  const sd = new Date(s); const ed = new Date(e);
  for (const d = new Date(sd); d <= ed; d.setDate(d.getDate() + 1)) {
    yield new Date(d).toISOString().slice(0, 10);
  }
}

function TeamVacationsCalendar({ overview, year }) {
  const [month, setMonth] = useState(new Date().getMonth()); // 0-11

  // Recoger eventos por día: {YYYY-MM-DD: [{name, status, role, color}]}
  const byDay = useMemo(() => {
    const acc = {};
    if (!overview) return acc;
    for (const p of overview.items) {
      for (const v of (p.vacations || [])) {
        if (v.status === "rejected") continue;
        for (const d of iterDates(v.start_date, v.end_date)) {
          acc[d] = acc[d] || [];
          acc[d].push({ name: p.name, status: v.status, role: p.role });
        }
      }
    }
    return acc;
  }, [overview]);

  // Si no hay datos en ningún día del año, todavía permitimos navegar.
  const firstDay = new Date(year, month, 1);
  const monthLabel = MONTH_LABELS_ES[month];
  const lastDay = new Date(year, month + 1, 0).getDate();
  // Lunes=0
  const firstDow = (firstDay.getDay() + 6) % 7;
  const today = new Date().toISOString().slice(0, 10);

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= lastDay; d++) {
    const dStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ day: d, dStr, persons: byDay[dStr] || [] });
  }
  while (cells.length % 7 !== 0) cells.push(null);

  const totalsThisMonth = useMemo(() => {
    const s = new Set();
    Object.entries(byDay).forEach(([d, arr]) => {
      if (d.startsWith(`${year}-${String(month + 1).padStart(2, "0")}`)) {
        arr.forEach((p) => s.add(p.name));
      }
    });
    return s.size;
  }, [byDay, year, month]);

  return (
    <div data-testid="team-vac-calendar">
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => setMonth((m) => (m === 0 ? 11 : m - 1))}
          className="px-2 py-1 text-sm rounded-md border border-border hover:bg-stone-50 text-muted"
          data-testid="team-vac-prev"
        >‹</button>
        <div className="text-[14px] font-bold capitalize">{monthLabel} {year}</div>
        <button
          onClick={() => setMonth((m) => (m === 11 ? 0 : m + 1))}
          className="px-2 py-1 text-sm rounded-md border border-border hover:bg-stone-50 text-muted"
          data-testid="team-vac-next"
        >›</button>
      </div>

      <div className="text-[11px] text-muted mb-2">
        {totalsThisMonth} {totalsThisMonth === 1 ? "persona" : "personas"} con vacaciones este mes
      </div>

      <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-wider text-muted font-semibold mb-1 text-center">
        {["L", "M", "X", "J", "V", "S", "D"].map((d) => (
          <div key={d} className="py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => {
          // Días vacíos de padding (antes del 1 del mes o después del último).
          // Como pueden repetirse `null` varias veces, usamos el índice
          // combinado con el mes/año para mantener un key estable entre
          // renders del mismo mes.
          if (!c) return <div key={`pad-${year}-${month}-${i}`} />;
          const isToday = c.dStr === today;
          const hasApproved = c.persons.some((p) => p.status === "approved");
          const hasPending = c.persons.some((p) => p.status === "requested");
          return (
            <div
              key={c.dStr}
              className={
                "min-h-[60px] rounded-md border p-1 text-[11px] " +
                (hasApproved ? "bg-emerald-50 border-emerald-200" :
                 hasPending ? "bg-amber-50 border-amber-200" :
                 "bg-white border-border") +
                (isToday ? " ring-1 ring-orange-400" : "")
              }
              data-testid={`team-vac-day-${c.dStr}`}
              title={c.persons.map((p) => `${p.name}${p.status === "requested" ? " (pendiente)" : ""}`).join("\n")}
            >
              <div className={`font-semibold ${isToday ? "text-orange-600" : "text-foreground"}`}>{c.day}</div>
              <div className="mt-0.5 space-y-0.5">
                {c.persons.slice(0, 3).map((p) => (
                  <div
                    key={`${c.dStr}-${p.name}-${p.status}`}
                    className={
                      "truncate text-[10px] leading-tight px-1 rounded " +
                      (p.status === "approved"
                        ? "bg-emerald-100 text-emerald-800"
                        : "bg-amber-100 text-amber-800 italic")
                    }
                  >{p.name.split(" ")[0]}</div>
                ))}
                {c.persons.length > 3 && (
                  <div className="text-[9px] text-muted">+{c.persons.length - 3} más</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex items-center gap-3 text-[11px] text-muted">
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-100 border border-emerald-300"></span>Aprobada</span>
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-amber-100 border border-amber-300"></span>Pendiente</span>
      </div>
    </div>
  );
}

function TeamVacationsBalances({ overview, canEdit, onChanged }) {
  const [editing, setEditing] = useState(null); // {perito_id, name, value}

  const submitEdit = async () => {
    if (!editing) return;
    const v = Number(editing.value);
    if (Number.isNaN(v) || v < 0 || v > 60) { toast.error("Días entre 0 y 60"); return; }
    try {
      await api.put(`/calendar/admin/peritos/${editing.perito_id}/vacation-days`, { vacation_days_per_year: v });
      toast.success(`Días anuales actualizados para ${editing.name}`);
      setEditing(null);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    }
  };

  const ROLE_LABEL = {
    admin: "Admin",
    gestor_senior: "Gestor senior",
    gestor_siniestros: "Gestor",
    perito_interno: "Perito",
    perito_colaborador: "Colaborador",
  };

  return (
    <>
      <div className="overflow-x-auto" data-testid="team-vac-balances">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-muted border-b border-border">
              <th className="py-2 pr-2">Persona</th>
              <th className="py-2 pr-2">Rol</th>
              <th className="py-2 pr-2 text-right">Asignados</th>
              <th className="py-2 pr-2 text-right">Usados</th>
              <th className="py-2 pr-2 text-right">Pendientes</th>
              <th className="py-2 pr-2 text-right">Restantes</th>
              <th className="py-2 pr-2 w-[30%]">Progreso</th>
              {canEdit && <th className="py-2 pr-2"></th>}
            </tr>
          </thead>
          <tbody>
            {overview?.items?.map((p) => {
              const pct = p.assigned > 0 ? Math.min(100, Math.round((p.used / p.assigned) * 100)) : 0;
              const barColor = pct >= 90 ? "bg-red-500" : pct >= 60 ? "bg-amber-500" : "bg-emerald-500";
              return (
                <tr key={p.perito_id} className="border-b border-border/50">
                  <td className="py-2 pr-2 font-semibold text-foreground">{p.name}</td>
                  <td className="py-2 pr-2 text-muted">{ROLE_LABEL[p.role] || p.role}</td>
                  <td className="py-2 pr-2 text-right tabular-nums">{p.assigned}</td>
                  <td className="py-2 pr-2 text-right tabular-nums">{p.used}</td>
                  <td className="py-2 pr-2 text-right tabular-nums text-amber-700">{p.pending || "—"}</td>
                  <td className={`py-2 pr-2 text-right tabular-nums font-bold ${p.remaining === 0 && p.assigned > 0 ? "text-red-600" : "text-emerald-700"}`}
                      data-testid={`balance-remaining-${p.perito_id}`}>
                    {p.remaining}
                  </td>
                  <td className="py-2 pr-2">
                    <div className="w-full bg-stone-100 rounded-full h-2 overflow-hidden">
                      <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }}></div>
                    </div>
                  </td>
                  {canEdit && (
                    <td className="py-2 pr-2">
                      <button
                        onClick={() => setEditing({ perito_id: p.perito_id, name: p.name, value: p.assigned })}
                        className="text-orange-600 hover:text-orange-700 p-1 rounded hover:bg-orange-50"
                        data-testid={`edit-vac-days-${p.perito_id}`}
                        title="Editar días anuales"
                      >
                        <PencilSimple className="w-4 h-4" />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
            {!overview?.items?.length && (
              <tr><td colSpan={canEdit ? 8 : 7} className="py-6 text-center text-muted">Sin datos.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-xl p-5 max-w-sm w-full" onClick={(e) => e.stopPropagation()} data-testid="edit-vac-modal">
            <h3 className="text-[15px] font-bold mb-1">Días de vacaciones anuales</h3>
            <p className="text-[12px] text-muted mb-3">Para <strong>{editing.name}</strong>. Días laborables (L-V).</p>
            <input
              type="number"
              min={0} max={60}
              value={editing.value}
              onChange={(e) => setEditing({ ...editing, value: e.target.value })}
              className="w-full border border-border rounded-md px-3 py-2 text-[14px]"
              data-testid="edit-vac-input"
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setEditing(null)} className="px-3 py-1.5 text-[13px] text-muted hover:text-foreground">Cancelar</button>
              <button onClick={submitEdit} className="px-4 py-1.5 text-[13px] bg-orange-600 text-white rounded-md hover:bg-orange-700 font-semibold" data-testid="edit-vac-save">
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
